import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  Select,
  MenuItem,
  Paper,
} from '@mui/material';
import { useTheme } from "@mui/material/styles";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { useDispatch, useSelector } from "react-redux";
import octopusInstance from "../../services/octopus/octopusInstance";
import { exchangeSpecificForOptionChain, getExchangeCode } from "../../services/exchanges";
import useTouchInteraction from "../../hooks/useTouchInteraction";
import { openOrderWindow } from "../../store/actions/orders";
import { getProductType } from "../../utils/utilfunction";
import { getLotSize } from "../../services/utilities/lotSize";
import ColumnFilter from "./ColumnFilter";

// Helper function to check if instrument is an index
const isIndex = (instrument) => {
  return instrument?.index === true;
};

const TouchableButton = React.memo(({ onClick, children, variant, disabled, color, sx }) => {
  const theme = useTheme();

  const handleClick = useCallback((e) => {
    if (!disabled && onClick) {
      e.preventDefault();
      e.stopPropagation();
      onClick(e);
    }
  }, [onClick, disabled]);

  return (
    <Button
      onClick={handleClick}
      variant={variant}
      disabled={disabled}
      sx={{
        ...sx,
        transition: 'all 0.2s ease',
        WebkitTapHighlightColor: 'transparent',
        pointerEvents: disabled ? 'none' : 'auto',
        '&:active': {
          backgroundColor: !disabled && variant === 'outlined'
            ? color === 'success'
              ? `${theme.palette.success.main}20`
              : `${theme.palette.error.main}20`
            : undefined,
          transform: 'scale(0.98)'
        }
      }}
    >
      {children}
    </Button>
  );
});

const TouchableSelect = React.memo(({ value, onChange, children, sx }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const { touchStates } = useTouchInteraction(handleOpen, true);

  return (
    <Select
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      value={value}
      onChange={(e) => {
        onChange(e);
        handleClose();
      }}
      variant="outlined"
      MenuProps={{
        PaperProps: {
          sx: {
            maxHeight: '40vh',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
            '& .MuiMenuItem-root': {
              minHeight: 42,
              fontSize: '0.875rem',
              transition: 'background-color 0.2s ease',
              WebkitTapHighlightColor: 'transparent',
              '&:active': {
                backgroundColor: 'action.hover'
              }
            }
          }
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'center'
        },
        sx: {
          mt: 1
        }
      }}
      sx={{
        ...sx,
        backgroundColor: touchStates.isActive && !touchStates.isTouchMoved
          ? 'action.hover'
          : undefined,
        transition: 'all 0.2s ease',
        WebkitTapHighlightColor: 'transparent',
        '& .MuiSelect-select': {
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          paddingRight: '28px !important',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: open ? theme.palette.primary.main : theme.palette.divider
        },
        ...sx
      }}
    >
      {children}
    </Select>
  );
});

// Add the date conversion function before the OptionsHeader component
const convertExpiryDate = (date) => {
  try {
    const match = date.match(/(\d{2})([A-Z]{3})(\d{2})/);
    if (!match) {
      console.warn('Invalid date format:', date);
      return null;
    }

    const [_, day, month, year] = match;
    const monthMap = {
      'JAN': 0, 'FEB': 1, 'MAR': 2, 'APR': 3, 'MAY': 4, 'JUN': 5,
      'JUL': 6, 'AUG': 7, 'SEP': 8, 'OCT': 9, 'NOV': 10, 'DEC': 11
    };

    const fullYear = 2000 + parseInt(year);
    const monthIndex = monthMap[month];
    const expiryDate = new Date(fullYear, monthIndex, parseInt(day));

    if (isNaN(expiryDate.getTime())) {
      console.warn('Invalid date:', date);
      return null;
    }

    const dayLabel = `${day}${getDayLabel(parseInt(day))}`;
    return {
      formattedDate: `${dayLabel} ${month} ${year}`,
      isValid: true
    };
  } catch (error) {
    console.warn('Error formatting date:', date, error);
    return {
      formattedDate: date,
      isValid: false
    };
  }
};

function OptionsHeader({
  display_name,
  exchange,
  trading_symbol,
  token,
  ltp,
  num,
  setNum,
  instrument,
  expiryDates,
  selectedExpiryDate,
  setSelectedExpiryDate,
  selectedColumns,
  setSelectedColumns,
  isIndex
}) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { userSettings } = useSelector((state) => state.profile);


  const [dynamicInfo, setDynamicInfo] = useState({
    ltp: ltp || 0,
    bestBid: instrument?.bestBidPrice || 0,
    bestAsk: instrument?.bestAskPrice || 0
  });

  const [ltpHigh, setLtpHigh] = useState(null);
  const [nearestStrike, setNearestStrike] = useState(null);

  // Use the isIndex prop or calculate if it is an index
  const isInstrumentIndex = isIndex || instrument?.is_index || instrument?.index === true || instrument?.series === "INDICES";
  console.log("isInstrumentIndex", isInstrumentIndex);

  // Find the nearest strike price to the current LTP
  useEffect(() => {
    if (instrument?.option_chain && dynamicInfo.ltp) {
      const strikes = instrument.option_chain.map(item => item.strike_price).filter(Boolean);

      if (strikes.length > 0) {
        let nearest = strikes[0];
        let minDiff = Math.abs(nearest - dynamicInfo.ltp);

        strikes.forEach(strike => {
          const diff = Math.abs(strike - dynamicInfo.ltp);
          if (diff < minDiff) {
            minDiff = diff;
            nearest = strike;
          }
        });

        setNearestStrike(nearest);
      }
    }
  }, [instrument, dynamicInfo.ltp]);

  useEffect(() => {
    if (!exchange || !token) return;

    let handler = null;
    try {
      // Create a unique subscription location using token
      const subscriptionLocation = `option-chain-header-${exchange}-${token}`;
      handler = octopusInstance.wsHandler({
        messageType: "CompactMarketDataMessage",
        subscriptionLocation,
        payload: {
          exchangeCode: getExchangeCode(exchange),
          instrumentToken: token,
        },
      });

      handler.subscribe(({ msg }) => {
        setDynamicInfo(prev => {
          const prevLtp = prev.ltp;
          const currLtp = msg.ltp || msg.lastTradedPrice || prev.ltp;

          if (prevLtp < currLtp) {
            setLtpHigh(true);
          } else if (prevLtp > currLtp) {
            setLtpHigh(false);
          }

          return {
            ...prev,
            ltp: currLtp,
            bestBid: msg.bestBidPrice || msg.bidPrice || prev.bestBid,
            bestAsk: msg.bestAskPrice || msg.askPrice || prev.bestAsk
          };
        });
      }).catch(err => {
        console.warn('Subscription error:', err);
      });
    } catch (err) {
      console.error('Handler setup error:', err);
    }

    // Cleanup function to unsubscribe
    return () => {
      if (handler) {
        handler.unsubscribe().catch(err => {
          console.warn('Unsubscribe error:', err);
        });
      }
    };
  }, [exchange, token]);

  const getInstrumentToken = useCallback(() => {
    if (exchange === "MCX" && trading_symbol?.startsWith("FUT")) {
      return instrument?.instrument_token;
    } else if (exchange === "BSE" && instrument?.is_index) {
      return instrument?.instrument_token;
    }
    return instrument?.instrument_token || token;
  }, [exchange, trading_symbol, instrument, token]);

  const handleBuy = useCallback((e) => {
    e?.preventDefault();
    e?.stopPropagation();

    const instrumentToken = getInstrumentToken();
    dispatch(openOrderWindow({
      type: 'BUY',
      trading_symbol,
      display_name,
      ltp: dynamicInfo.ltp,
      product: getProductType(userSettings.product_type, exchange),
      exchange: exchange,
      token: instrumentToken,
      lotsize: getLotSize(instrument),
      tick_size: instrument?.tick_size,
      execution: instrument?.execution || "REGULAR",
      bestBidPrice: dynamicInfo.bestBid,
      bestAskPrice: dynamicInfo.bestAsk,
      order_type: userSettings.order_type
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, display_name, exchange, trading_symbol, dynamicInfo, instrument, getInstrumentToken]);

  const handleSell = useCallback((e) => {
    e?.preventDefault();
    e?.stopPropagation();

    const instrumentToken = getInstrumentToken();
    dispatch(openOrderWindow({
      type: 'SELL',
      trading_symbol,
      display_name,
      ltp: dynamicInfo.ltp,
      exchange: exchange,
      token: instrumentToken,
      lotsize: getLotSize(instrument),
      tick_size: instrument?.tick_size,
      execution: instrument?.execution || "REGULAR",
      bestBidPrice: dynamicInfo.bestBid,
      bestAskPrice: dynamicInfo.bestAsk,
      product: getProductType(userSettings.product_type, exchange),
      order_type: userSettings.order_type
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, display_name, exchange, trading_symbol, dynamicInfo, instrument, getInstrumentToken]);

  useEffect(() => {
    if (expiryDates?.length > 0 && (!selectedExpiryDate || !expiryDates.includes(selectedExpiryDate))) {
      setSelectedExpiryDate(expiryDates[0]);
    }
  }, [expiryDates]);

  return (
    <Paper
      elevation={0}
      sx={{
        px: { xs: 1, sm: 1.5 },
        pt: { xs: 0, sm: 1 },
        pb: { xs: 0, sm: 1 },
        mt: { xs: 0, sm: 0.5 },
        mb: 1,
        borderRadius: { xs: 0, sm: 2 },
        position: 'relative',
        zIndex: 0,
        width: '100%',
        background: theme.palette.mode === 'dark'
          ? "linear-gradient(135deg, rgba(10, 25, 41, 0.8) 0%, rgba(10, 25, 41, 0.9) 100%)"
          : "linear-gradient(135deg, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0.05) 100%)",
        backdropFilter: { xs: 'none', sm: "blur(15px)" },
        border: `1px solid ${theme.palette.mode === 'dark'
          ? 'rgba(255,255,255,0.12)'
          : theme.palette.divider}`,
        boxShadow: theme.palette.mode === 'dark'
          ? '0 4px 6px rgba(0,0,0,0.1)'
          : '0 4px 6px rgba(0,0,0,0.05)'
      }}
    >
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'center' },
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        width: '100%',
        gap: { xs: 0.5, sm: 1, md: 1.5 },
        pt: { xs: 0.5, sm: 0.75, md: 1 },
        pb: { xs: 0.5, sm: 0.75, md: 1 },
        position: 'relative',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' }
      }}>
        {/* Instrument Information Section */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: { xs: 'space-between', md: 'flex-start' },
          width: { xs: '100%', md: 'auto' },
          flexGrow: 0,
          flexShrink: 1,
          minWidth: 0
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.3, sm: 1, md: 2 },
            justifyContent: 'flex-start',
            minWidth: 0,
            flex: 1
          }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1.1rem" },
                color: theme.palette.text.primary,
                letterSpacing: "0.5px",
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {display_name || trading_symbol}
            </Typography>

            <Box sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              py: 0.3,
              px: 1,
              borderRadius: 1.5,
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.05)'
                : 'rgba(0,0,0,0.03)',
              color: ltpHigh === null
                ? theme.palette.text.primary
                : ltpHigh
                  ? theme.palette.success.main
                  : theme.palette.error.main,
              transition: "all 0.3s ease"
            }}>
              {ltpHigh !== null && (
                ltpHigh ?
                  <TrendingUpIcon sx={{ fontSize: { xs: "0.8rem", sm: "0.95rem" } }} /> :
                  <TrendingDownIcon sx={{ fontSize: { xs: "0.8rem", sm: "0.95rem" } }} />
              )}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.7rem", sm: "0.85rem" },
                  fontFamily: "monospace",
                  minWidth: { xs: '35px', sm: '45px' },
                  textAlign: 'right'
                }}
              >
                {exchangeSpecificForOptionChain(exchange, dynamicInfo.ltp)}
              </Typography>
            </Box>

            {nearestStrike && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: "center",
                  gap: 0.5,
                  py: 0.8,
                  px: 1.8,
                  borderRadius: 2,
                  position: 'relative',
                  overflow: 'hidden',
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(25, 118, 210, 0.15)'
                    : 'rgba(25, 118, 210, 0.08)',
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 0 8px ${theme.palette.primary.main}30`,
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
                  }
                }}
              >
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  position: 'relative',
                  pr: { xs: 0, sm: 1.5 },
                  mr: { xs: 0, sm: 1 },
                  '&::after': {
                    content: { xs: 'none', sm: '""' },
                    position: 'absolute',
                    right: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '1px',
                    height: '70%',
                    background: theme.palette.divider
                  }
                }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: "0.85rem", sm: "0.95rem" },
                      color: theme.palette.primary.main,
                      letterSpacing: '0.3px'
                    }}
                  >
                    ATM
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: "0.85rem", sm: "0.95rem" },
                      fontFamily: 'monospace',
                      color: theme.palette.text.primary
                    }}
                  >
                    {nearestStrike}
                  </Typography>
                </Box>

                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: { xs: 'center', sm: 'flex-start' },
                  justifyContent: 'center',
                  mt: { xs: 0.3, sm: 0 }
                }}>
                  {dynamicInfo.ltp && nearestStrike && (
                    <>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.7rem',
                          color: theme.palette.text.secondary,
                          lineHeight: 1.1
                        }}
                      >
                        Diff from spot:
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          lineHeight: 1.2,
                          color: Math.abs(((nearestStrike - dynamicInfo.ltp) / dynamicInfo.ltp) * 100) < 0.5
                            ? theme.palette.success.main
                            : Math.abs(((nearestStrike - dynamicInfo.ltp) / dynamicInfo.ltp) * 100) < 1
                              ? theme.palette.warning.main
                              : theme.palette.text.primary
                        }}
                      >
                        {((nearestStrike - dynamicInfo.ltp) / dynamicInfo.ltp * 100).toFixed(2)}%
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            )}
          </Box>

          {/* Buy/Sell Buttons Row */}
          {!isInstrumentIndex && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.3, sm: 0.5, md: 1 },
              width: 'auto',
              justifyContent: 'flex-end',
              flexShrink: 0,
              ml: { xs: 1, sm: 2, md: 3 }
            }}>
              <TouchableButton
                onClick={handleBuy}
                variant="outlined"
                disabled={!instrument?.tradable}
                color="success"
                sx={{
                  flex: 'none',
                  minWidth: { xs: 40, sm: 48, md: 55 },
                  height: { xs: 24, sm: 26, md: 28 },
                  fontWeight: 600,
                  fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                  px: { xs: 0.5, sm: 1 },
                  borderColor: theme.palette.success.main,
                  color: theme.palette.success.main,
                  borderRadius: { xs: 1, sm: 1.5 },
                  "&:hover": {
                    bgcolor: `${theme.palette.success.main}10`,
                    borderColor: theme.palette.success.dark
                  }
                }}
              >
                BUY
              </TouchableButton>

              <TouchableButton
                onClick={handleSell}
                variant="outlined"
                disabled={!instrument?.tradable}
                color="error"
                sx={{
                  flex: 'none',
                  minWidth: { xs: 40, sm: 48, md: 55 },
                  height: { xs: 24, sm: 26, md: 28 },
                  fontWeight: 600,
                  fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                  px: { xs: 0.5, sm: 1 },
                  borderColor: theme.palette.error.main,
                  color: theme.palette.error.main,
                  borderRadius: { xs: 1, sm: 1.5 },
                  "&:hover": {
                    bgcolor: `${theme.palette.error.main}10`,
                    borderColor: theme.palette.error.dark
                  }
                }}
              >
                SELL
              </TouchableButton>
            </Box>
          )}
        </Box>

        {/* Action Buttons Section */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: { xs: 'space-between', md: 'flex-end' },
          width: { xs: '100%', md: 'auto' },
          gap: { xs: 0.3, sm: 1, md: 1.5 },
          flexDirection: 'row',
          flexWrap: 'wrap',
          flexGrow: 0,
          flexShrink: 1,
          minWidth: 0,
          mt: { xs: 0.5, md: 0 }
        }}>
          {/* Controls Row */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.5, sm: 0.75, md: 1 },
            width: { xs: '100%', md: 'auto' },
            justifyContent: { xs: 'space-between', md: 'flex-end' },
            flexShrink: 1,
            flexWrap: 'wrap',
            minWidth: 0
          }}>
            {/* Expiry Date Dropdown */}
            <TouchableSelect
              value={selectedExpiryDate || ''}
              onChange={(e) => setSelectedExpiryDate(e.target.value)}
              sx={{
                flex: { xs: 4, sm: 'auto', md: 'none' },
                height: { xs: 24, sm: 26, md: 28 },
                width: 'auto',
                minWidth: { xs: 85, sm: 95, md: 105 },
                fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                borderRadius: { xs: 1, sm: 1.5 },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.divider
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.primary.main
                },
                "& .MuiSelect-select": {
                  py: { xs: 0.2, sm: 0.5 },
                  pl: { xs: 1, sm: 1.2 }
                }
              }}
            >
              {expiryDates?.length > 0 ? (
                expiryDates.map((date) => {
                  if (!date) return null;

                  const result = convertExpiryDate(date);
                  if (!result?.isValid) return null;

                  return (
                    <MenuItem
                      key={date}
                      value={date}
                      sx={{
                        fontSize: '0.875rem',
                        py: 1.5,
                        px: 2,
                        minHeight: 'auto',
                        backgroundColor: 'transparent',
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover
                        },
                        '&.Mui-selected': {
                          backgroundColor: theme.palette.action.selected,
                          '&:hover': {
                            backgroundColor: theme.palette.action.selected
                          }
                        }
                      }}
                    >
                      {result.formattedDate}
                    </MenuItem>
                  );
                })
              ) : (
                <MenuItem disabled value="">
                  <Typography variant="body2" color="textSecondary">
                    No dates available
                  </Typography>
                </MenuItem>
              )}
            </TouchableSelect>

            {/* Strikes Dropdown */}
            <TouchableSelect
              value={num}
              onChange={(e) => setNum(e.target.value)}
              sx={{
                flex: { xs: 3, sm: 'auto', md: 'none' },
                height: { xs: 24, sm: 26, md: 28 },
                width: 'auto',
                minWidth: { xs: 75, sm: 85, md: 90 },
                fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                borderRadius: { xs: 1, sm: 1.5 },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.divider
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.primary.main
                },
                "& .MuiSelect-select": {
                  py: { xs: 0.2, sm: 0.5 },
                  pl: { xs: 1, sm: 1.2 }
                }
              }}
            >
              <MenuItem value={6}>6 Strikes</MenuItem>
              <MenuItem value={12}>12 Strikes</MenuItem>
              <MenuItem value={18}>18 Strikes</MenuItem>
            </TouchableSelect>

            {/* Column Filter Dropdown */}
            <ColumnFilter
              selectedColumns={selectedColumns}
              setSelectedColumns={setSelectedColumns}
              sx={{ flex: { xs: 2.5, sm: 'auto', md: 'none' } }}
            />
          </Box>

        </Box>
      </Box>
    </Paper>
  );
}

const getDayLabel = (day) => {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

export default OptionsHeader; 